import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import PatientService from '../../services/PatientService';

const PatientList = () => {
  const navigate = useNavigate();
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('firstName');
  const [order, setOrder] = useState('asc');
  
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // PatientService is already an instance, no need to instantiate it
        const response = await PatientService.getAllPatients();
        const patientsData = Array.isArray(response.data) ? response.data : [];
        
        setPatients(patientsData);
        setFilteredPatients(patientsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setError('Failed to load patients. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, []);
  
useEffect(() => {
  const applyFilter = async () => {
    let baseList = Array.isArray(patients) ? patients : [];
    if (searchTerm) {
      try {
        const response = await PatientService.searchPatients(searchTerm);
        baseList = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        baseList = [];
      }
    }
    let filtered = baseList;
    if (showActiveOnly) {
      filtered = filtered.filter(patient => patient.isActive);
    }
    setFilteredPatients(filtered);
    setPage(0);
  };
  applyFilter();
}, [searchTerm, showActiveOnly, patients]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => sortComparator(a, b, orderBy)
      : (a, b) => -sortComparator(a, b, orderBy);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  const toggleActiveFilter = () => {
    setShowActiveOnly(!showActiveOnly);
  };
  
  const handleViewPatient = (patientId) => {
    navigate(`/patients/${patientId}`);
  };
  
  const handleEditPatient = (patientId) => {
    navigate(`/patients/${patientId}/edit`);
  };
  
  const handleDeletePatient = async (patientId) => {
    // In a real application, you would call your API to delete the patient
    // For now, we'll just filter the patient from the local state
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setPatients(patients.filter(patient => patient.patientId !== patientId));
      } catch (error) {
        console.error('Error deleting patient:', error);
        alert('Failed to delete patient. Please try again.');
      }
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Patients
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/patients/new')}
        >
          Add Patient
        </Button>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search patients..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant={showActiveOnly ? "contained" : "outlined"}
                color={showActiveOnly ? "primary" : "inherit"}
                startIcon={<FilterListIcon />}
                onClick={toggleActiveFilter}
                sx={{ mr: 1 }}
              >
                Active Only
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="patients table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'firstName'}
                    direction={orderBy === 'firstName' ? order : 'asc'}
                    onClick={() => handleRequestSort('firstName')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'dateOfBirth'}
                    direction={orderBy === 'dateOfBirth' ? order : 'asc'}
                    onClick={() => handleRequestSort('dateOfBirth')}
                  >
                    Date of Birth
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'gender'}
                    direction={orderBy === 'gender' ? order : 'asc'}
                    onClick={() => handleRequestSort('gender')}
                  >
                    Gender
                  </TableSortLabel>
                </TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'isActive'}
                    direction={orderBy === 'isActive' ? order : 'asc'}
                    onClick={() => handleRequestSort('isActive')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPatients
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((patient) => {
                  const fullName = `${patient.firstName} ${patient.lastName}`;
                  
                  return (
                    <TableRow hover key={patient.patientId}>
                      <TableCell component="th" scope="row">
                        {fullName}
                      </TableCell>
                      <TableCell>
                        {new Date(patient.dateOfBirth).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{patient.gender}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{patient.country || 'N/A'}</Typography>
                        <Typography variant="body2" color="textSecondary">{patient.state}, {patient.city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{patient.phoneNumber}</Typography>
                        <Typography variant="body2" color="textSecondary">{patient.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={patient.isActive ? "Active" : "Inactive"}
                          color={patient.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewPatient(patient.patientId)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditPatient(patient.patientId)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePatient(patient.patientId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filteredPatients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No patients found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPatients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default PatientList;